<template>
  <div class="et-root" :dir="t.dir">

    <!-- HEADER -->
    <div class="et-header">
      <div class="et-header-title">{{ gameName || navigationName }}</div>
      <div class="et-header-meta">
        <span class="et-badge">{{ t.participants(activeUsers.length) }}</span>
        <span class="et-badge et-badge-done">{{ t.done(finishedUsers.length) }}</span>
      </div>
      <div class="et-updated">⟳ {{ lastUpdated }}</div>
    </div>

    <!-- DATE FILTER -->
    <div class="et-date-filter">
      <div class="et-date-field">
        <label class="et-date-label">{{ t.from }}</label>
        <input type="date" v-model="dateFrom" class="et-date-input" />
      </div>
      <div class="et-date-sep">→</div>
      <div class="et-date-field">
        <label class="et-date-label">{{ t.to }}</label>
        <input type="date" v-model="dateTo" class="et-date-input" />
      </div>
      <button v-if="dateFrom || dateTo" class="et-date-clear" @click="dateFrom = ''; dateTo = ''">✕</button>
    </div>

    <!-- TABS -->
    <div class="et-tabs">
      <button :class="['et-tab', { active: activeTab === 'progress' }]" @click="activeTab = 'progress'">
        {{ t.inProgress }} <span class="et-tab-count">{{ activeUsers.length }}</span>
      </button>
      <button :class="['et-tab', { active: activeTab === 'done' }]" @click="activeTab = 'done'">
        {{ t.finished }} <span class="et-tab-count">{{ finishedUsers.length }}</span>
      </button>
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="et-loading">
      <div class="et-spinner"></div>
    </div>

    <!-- TABLE -->
    <div v-else class="et-list">

      <!-- In progress tab -->
      <template v-if="activeTab === 'progress'">
        <div v-if="activeUsers.length === 0" class="et-empty">{{ t.noActive }}</div>
        <div v-for="(user, i) in activeUsers" :key="user.key" class="et-card">
          <div class="et-rank et-rank-num">#{{ i + 1 }}</div>
          <div class="et-info">
            <div class="et-name">{{ user.userName }}</div>
            <div class="et-progress-row">
              <div class="et-progress-bar">
                <div class="et-progress-fill" :style="{ width: progressPct(user.evolution) + '%' }"></div>
              </div>
              <span class="et-progress-label">{{ user.evolution }} / {{ totalPoints }}</span>
            </div>
            <div class="et-meta-row">
              <span class="et-chip">🚩 {{ t.point }} {{ user.evolution }}</span>
              <span v-if="user.score !== null" class="et-chip et-chip-score">⭐ {{ user.score }} pts</span>
              <span v-if="user.elapsed" class="et-chip">⏱ {{ user.elapsed }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Finished tab -->
      <template v-if="activeTab === 'done'">
        <div v-if="finishedUsers.length === 0" class="et-empty">{{ t.noDone }}</div>
        <div v-for="(user, i) in finishedUsers" :key="'f' + user.key" class="et-card et-card-done">
          <div class="et-rank">
            <span v-if="i === 0">🥇</span>
            <span v-else-if="i === 1">🥈</span>
            <span v-else-if="i === 2">🥉</span>
            <span v-else class="et-rank-num">#{{ i + 1 }}</span>
          </div>
          <div class="et-info">
            <div class="et-name">{{ user.userName }}</div>
            <div class="et-meta-row">
              <span class="et-chip et-chip-score">⭐ {{ user.score }} pts</span>
              <span v-if="user.gameTime" class="et-chip">⏱ {{ user.gameTime }}</span>
            </div>
          </div>
        </div>
      </template>

    </div>

    <!-- BACK BUTTON -->
    <button class="et-back-btn" @click="goToHomePage">{{ t.back }}</button>

  </div>
</template>

<script>
/* eslint-disable */
import firebase from 'firebase/app';
import 'firebase/database';

export default {
  data() {
    return {
      navigationName: '',
      gameName: '',
      totalPoints: 0,
      rawUsers: [],
      rawResults: {},
      loading: true,
      lastUpdated: '—',
      usersRef: null,
      resultsRef: null,
      dateFrom: '',
      dateTo: '',
      lang: 'fr',
      activeTab: 'progress',
    };
  },

  computed: {
    t() {
      const map = {
        fr: {
          inProgress: '🏃 En cours',
          finished: '🏁 Terminés',
          noActive: 'Aucun participant en cours',
          noDone: 'Aucun participant n\'a encore terminé',
          participants: n => `${n} participant${n > 1 ? 's' : ''}`,
          done: n => `${n} terminé${n > 1 ? 's' : ''}`,
          point: 'Point',
          status_active: 'En cours',
          status_done: 'Terminé ✓',
          from: 'Du',
          to: 'Au',
          back: '← Retour',
          dir: 'ltr',
        },
        he: {
          inProgress: '🏃 בתהליך',
          finished: '🏁 סיימו',
          noActive: 'אין משתתפים פעילים כרגע',
          noDone: 'אף משתתף לא סיים עדיין',
          participants: n => `${n} משתתפ${n > 1 ? 'ים' : ''}`,
          done: n => `${n} סי${n > 1 ? 'ימו' : 'ים'}`,
          point: 'תחנה',
          status_active: 'בתהליך',
          status_done: 'סיים ✓',
          from: 'מ',
          to: 'עד',
          back: 'חזרה →',
          dir: 'rtl',
        },
        en: {
          inProgress: '🏃 In progress',
          finished: '🏁 Finished',
          noActive: 'No active participants',
          noDone: 'No one has finished yet',
          participants: n => `${n} participant${n > 1 ? 's' : ''}`,
          done: n => `${n} finished`,
          point: 'Point',
          status_active: 'In progress',
          status_done: 'Done ✓',
          from: 'From',
          to: 'To',
          back: '← Back',
          dir: 'ltr',
        },
        ar: {
          inProgress: '🏃 قيد التقدم',
          finished: '🏁 انتهوا',
          noActive: 'لا يوجد مشاركون نشطون حالياً',
          noDone: 'لم ينته أي مشارك بعد',
          participants: n => `${n} مشارك`,
          done: n => `${n} انتهوا`,
          point: 'نقطة',
          status_active: 'قيد التقدم',
          status_done: 'انتهى ✓',
          from: 'من',
          to: 'إلى',
          back: 'رجوع →',
          dir: 'rtl',
        },
        de: {
          inProgress: '🏃 Laufend',
          finished: '🏁 Beendet',
          noActive: 'Keine aktiven Teilnehmer',
          noDone: 'Noch niemand hat beendet',
          participants: n => `${n} Teilnehmer`,
          done: n => `${n} beendet`,
          point: 'Punkt',
          status_active: 'Laufend',
          status_done: 'Fertig ✓',
          from: 'Von',
          to: 'Bis',
          back: '← Zurück',
          dir: 'ltr',
        },
      };
      return map[this.lang] || map.fr;
    },

    allMerged() {
      const from = this.dateFrom ? new Date(this.dateFrom).setHours(0,0,0,0) : null;
      const to   = this.dateTo   ? new Date(this.dateTo).setHours(23,59,59,999) : null;

      return this.rawUsers
        .filter(u => {
          if (!from && !to) return true;
          const t = u.startTime ? new Date(u.startTime).getTime() : null;
          if (!t) return false;
          if (from && t < from) return false;
          if (to   && t > to)   return false;
          return true;
        })
        .map(u => {
          const result = this.rawResults[u.key] || null;
          const elapsed = u.startTime ? this.formatElapsed(u.startTime) : null;
          return {
            ...u,
            score: result ? result.errorNumber : (u.errorNumber !== undefined ? u.errorNumber : null),
            gameTime: result ? result.secondsToTime : null,
            finished: !!result,
            elapsed,
          };
        });
    },

    activeUsers() {
      return this.allMerged
        .filter(u => !u.finished)
        .sort((a, b) => (b.evolution || 0) - (a.evolution || 0));
    },

    finishedUsers() {
      return this.allMerged
        .filter(u => u.finished)
        .sort((a, b) => (b.score || 0) - (a.score || 0));
    },
  },

  mounted() {
    this.navigationName = localStorage.getItem('navigationName') || '';
    this.listenUsers();
    this.listenResults();
    this.fetchGameMeta();
  },

  beforeDestroy() {
    if (this.usersRef) this.usersRef.off();
    if (this.resultsRef) this.resultsRef.off();
  },

  methods: {
    listenUsers() {
      const db = firebase.database();
      this.usersRef = db.ref(this.navigationName + '/users');
      this.usersRef.on('value', snap => {
        const arr = [];
        snap.forEach(child => {
          const v = child.val();
          if (v.userName) {
            arr.push({
              key: child.key,
              userName: v.userName,
              evolution: v.evolution !== undefined ? parseInt(v.evolution) + 1 : 1,
              startTime: v.NavigationTime || null,
              errorNumber: v.errorNumber !== undefined ? v.errorNumber : null,
            });
          }
        });
        this.rawUsers = arr;
        this.loading = false;
        this.updateTimestamp();
      });
    },

    listenResults() {
      const db = firebase.database();
      this.resultsRef = db.ref(this.navigationName + '/results');
      this.resultsRef.on('value', snap => {
        const map = {};
        snap.forEach(child => {
          map[child.key] = child.val();
        });
        this.rawResults = map;
        this.updateTimestamp();
      });
    },

    fetchGameMeta() {
      const db = firebase.database();
      // Get game display name
      db.ref(this.navigationName + '/displayName').once('value', snap => {
        if (snap.val()) this.gameName = snap.val();
      });
      // Get default language (first in the languages array)
      db.ref(this.navigationName + '/languages').once('value', snap => {
        if (snap.exists()) {
          const langs = snap.val();
          const first = Array.isArray(langs) ? langs[0] : Object.values(langs)[0];
          if (first) this.lang = first;
        }
      });
      // Count from pointsOrder — take the first entry and count its items
      // (all entries have the same number of points, just in different order)
      db.ref(this.navigationName + '/pointsOrder').once('value', snap => {
        if (snap.exists()) {
          const firstOrder = Object.values(snap.val())[0];
          if (Array.isArray(firstOrder)) {
            this.totalPoints = firstOrder.length;
          } else if (firstOrder && typeof firstOrder === 'object') {
            this.totalPoints = Object.keys(firstOrder).length;
          }
        }
      });
    },

    progressPct(evolution) {
      if (!this.totalPoints) return 0;
      // Cap at 100% — if evolution > totalPoints it means a test run with old config
      return Math.min(100, Math.round(((evolution || 1) / this.totalPoints) * 100));
    },

    formatElapsed(startTimeStr) {
      if (!startTimeStr) return null;
      const start = new Date(startTimeStr);
      if (isNaN(start)) return null;
      const diffMs = Date.now() - start.getTime();
      const totalMin = Math.floor(diffMs / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      if (h > 0) return `${h}h${m.toString().padStart(2, '0')}`;
      return `${m} min`;
    },

    updateTimestamp() {
      const now = new Date();
      this.lastUpdated = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },

    goToHomePage() {
      this.$router.push('/' + this.navigationName);
    },
  },
};
</script>

<style scoped>
.et-root {
  min-height: 100vh;
  background: #0f1117;
  padding: 70px 16px 100px;
  font-family: VarelaRound, system-ui, sans-serif;
  color: white;
}

/* ── Header ── */
.et-header {
  text-align: center;
  margin-bottom: 24px;
}
.et-header-title {
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 10px;
  letter-spacing: 0.3px;
}
.et-header-meta {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 6px;
}
.et-badge {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 50px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 600;
}
.et-badge-done {
  background: rgba(34,197,94,0.15);
  border-color: rgba(34,197,94,0.3);
  color: #4ade80;
}
.et-updated {
  font-size: 11px;
  color: rgba(255,255,255,0.35);
  margin-top: 4px;
}

/* ── Tabs ── */
.et-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
}
.et-tab {
  flex: 1;
  padding: 10px 12px;
  background: #1c1f2e;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  color: rgba(255,255,255,0.45);
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s;
}
.et-tab.active {
  background: #252a3d;
  border-color: rgba(56,178,248,0.4);
  color: white;
}
.et-tab-count {
  background: rgba(255,255,255,0.12);
  border-radius: 50px;
  padding: 1px 8px;
  font-size: 12px;
}
.et-tab.active .et-tab-count {
  background: rgba(56,178,248,0.25);
  color: #38b2f8;
}

/* ── Date filter ── */
.et-date-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #1c1f2e;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  padding: 12px 16px;
  margin-bottom: 20px;
}
.et-date-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
}
.et-date-label {
  font-size: 11px;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}
.et-date-input {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: white;
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 13px;
  padding: 6px 10px;
  width: 100%;
  box-sizing: border-box;
}
.et-date-input::-webkit-calendar-picker-indicator {
  filter: invert(1) opacity(0.5);
}
.et-date-sep {
  color: rgba(255,255,255,0.3);
  font-size: 18px;
  padding-top: 14px;
}
.et-date-clear {
  background: rgba(255,80,80,0.15);
  border: 1px solid rgba(255,80,80,0.25);
  border-radius: 50%;
  color: #f87171;
  width: 30px;
  height: 30px;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  align-self: center;
  margin-top: 14px;
}

/* ── Loading ── */
.et-loading {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}
.et-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: #38b2f8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Section label ── */
.et-section-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255,255,255,0.45);
  margin-bottom: 10px;
}
.et-empty {
  color: rgba(255,255,255,0.3);
  font-size: 14px;
  text-align: center;
  padding: 20px 0;
}

/* ── Card ── */
.et-card {
  background: #1c1f2e;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.et-card-done {
  background: #141a14;
  border-color: rgba(74,222,128,0.15);
}

/* ── Rank ── */
.et-rank {
  font-size: 26px;
  min-width: 36px;
  text-align: center;
  line-height: 1;
}
.et-rank-num {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255,255,255,0.4);
}

/* ── Info ── */
.et-info {
  flex: 1;
  min-width: 0;
}
.et-name {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Progress bar ── */
.et-progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.et-progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(255,255,255,0.1);
  border-radius: 50px;
  overflow: hidden;
}
.et-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #38b2f8, #1565c0);
  border-radius: 50px;
  transition: width 0.5s ease;
}
.et-progress-label {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  white-space: nowrap;
}

/* ── Chips ── */
.et-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.et-chip {
  background: rgba(255,255,255,0.07);
  border-radius: 50px;
  padding: 2px 10px;
  font-size: 12px;
  color: rgba(255,255,255,0.65);
}
.et-chip-score {
  background: rgba(250,204,21,0.12);
  color: #fbbf24;
}

/* ── Status badge ── */
.et-status {
  font-size: 11px;
  font-weight: 700;
  border-radius: 50px;
  padding: 4px 10px;
  white-space: nowrap;
}
.et-status-active {
  background: rgba(56,178,248,0.15);
  color: #38b2f8;
}
.et-status-done {
  background: rgba(74,222,128,0.15);
  color: #4ade80;
}

/* ── Back button ── */
.et-back-btn {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 50px;
  color: white;
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 36px;
  cursor: pointer;
}
.et-back-btn:active { opacity: 0.7; }
</style>
